import L from "leaflet";
import type { CatalogueEntry } from "../catalogue/types";
import { createEmitter } from "../core/events";
import type { Emitter } from "../core/events";
import type { LayerSnapshot, LayerStatus } from "../core/store";
import type { LayerSource } from "../sources/types";
import { zIndexBands } from "../sources/types";
import { sourceKindOf } from "../sources/registry";

export type RegistryEntry = {
  id: string;
  key: string;
  root: LayerSnapshot["root"];
  kind: string;
  title: string;
  entry: CatalogueEntry;
  source: LayerSource;
  selected: boolean;
  opacity: number;
  count: number | null;
  countLabel: string | null;
  status: LayerStatus;
  error: string | null;
  httpStatus: number | null;
  minZoom: number | null;
  maxZoom: number | null;
  truncated: boolean;
  disabledReason: string | null;
  zIndex: number;
  pane: string | null;
};

export type LayerChangeReason = "selected" | "opacity" | "labels" | "added" | "removed";

export type LayerEvents = {
  change: { entry: LayerSnapshot; reason: LayerChangeReason };
  list: { entries: LayerSnapshot[] };
  loaded: { entries: LayerSnapshot[] };
  error: { entry: LayerSnapshot; status: number | null; message: string };
};

export function paneNameOf(entry: CatalogueEntry): string {
  return `fxmap-layer-${entry.origin === "host" ? "host" : entry.root}-${entry.key}`;
}

export function zIndexOf(entry: CatalogueEntry, index: number): number {
  const kind = sourceKindOf(entry);

  switch (kind) {
    case "tile":
    case "special":
      return zIndexBands.base + index;
    case "tiles":
      return zIndexBands.overlay + index;
    case "vector":
      return zIndexBands.vector + index;
    case "track":
      return zIndexBands.track + index;
    default:
      return zIndexBands.custom + index;
  }
}

export function snapshotOf(entry: RegistryEntry): LayerSnapshot {
  return {
    id: entry.id,
    key: entry.key,
    root: entry.root,
    kind: entry.kind,
    title: entry.title,
    selected: entry.selected,
    opacity: entry.opacity,
    count: entry.count,
    countLabel: entry.countLabel,
    status: entry.status,
    error: entry.error,
    httpStatus: entry.httpStatus,
    minZoom: entry.minZoom,
    maxZoom: entry.maxZoom,
    truncated: entry.truncated,
    disabledReason: entry.disabledReason
  };
}

const originalFill = new WeakMap<L.Path, number>();
const originalStroke = new WeakMap<L.Path, number>();

export function applyOpacity(layer: L.Layer, value: number): void {
  if (layer instanceof L.LayerGroup) {
    layer.eachLayer(a => applyOpacity(a, value));
    return;
  }

  if (layer instanceof L.Marker || layer instanceof L.TileLayer || layer instanceof L.ImageOverlay) {
    layer.setOpacity(value);
    return;
  }

  if (layer instanceof L.Path) {
    let fill = originalFill.get(layer);
    let stroke = originalStroke.get(layer);

    if (fill === undefined) {
      fill = layer.options.fillOpacity ?? 0.2;
      originalFill.set(layer, fill);
    }

    if (stroke === undefined) {
      stroke = layer.options.opacity ?? 1;
      originalStroke.set(layer, stroke);
    }

    layer.setStyle({ opacity: stroke * value, fillOpacity: fill * value });
  }
}

export class LayerRegistry {
  readonly events: Emitter<LayerEvents> = createEmitter<LayerEvents>();

  private readonly map: L.Map;
  private readonly entries = new Map<string, RegistryEntry>();
  private readonly order: string[] = [];

  constructor(map: L.Map) {
    this.map = map;
  }

  ensurePane(name: string, zIndex: number): HTMLElement {
    const existing = this.map.getPane(name);

    if (existing) return existing;

    const pane = this.map.createPane(name);

    pane.style.zIndex = String(zIndex);

    return pane;
  }

  add(entry: RegistryEntry): LayerSnapshot {
    if (this.entries.has(entry.id)) this.remove(entry.id);

    if (entry.pane !== null) this.ensurePane(entry.pane, entry.zIndex);

    this.entries.set(entry.id, entry);
    this.order.push(entry.id);
    this.applyOpacityTo(entry);

    if (entry.selected && entry.disabledReason === null) this.map.addLayer(entry.source.layer);

    const snapshot = snapshotOf(entry);

    this.events.emit("change", { entry: snapshot, reason: "added" });
    this.emitList();

    return snapshot;
  }

  remove(id: string): void {
    const entry = this.entries.get(id);

    if (entry === undefined) return;

    if (this.map.hasLayer(entry.source.layer)) this.map.removeLayer(entry.source.layer);

    entry.source.dispose();
    this.entries.delete(id);
    this.order.splice(this.order.indexOf(id), 1);
    this.events.emit("change", { entry: snapshotOf(entry), reason: "removed" });
    this.emitList();
  }

  has(id: string): boolean {
    return this.entries.has(id);
  }

  get(id: string): RegistryEntry | null {
    return this.entries.get(id) ?? null;
  }

  byKey(root: LayerSnapshot["root"] | null, key: string): RegistryEntry | null {
    return this.list().find(a => a.key === key && (root === null || a.root === root)) ?? null;
  }

  snapshot(id: string): LayerSnapshot | null {
    const entry = this.entries.get(id);

    return entry === undefined ? null : snapshotOf(entry);
  }

  list(root?: LayerSnapshot["root"]): RegistryEntry[] {
    const all = this.order.map(a => this.entries.get(a)).filter((a): a is RegistryEntry => a !== undefined);

    return root === undefined ? all : all.filter(a => a.root === root);
  }

  snapshots(root?: LayerSnapshot["root"]): LayerSnapshot[] {
    return this.list(root).map(snapshotOf);
  }

  selectedIds(root?: LayerSnapshot["root"]): string[] {
    return this.list(root)
      .filter(a => a.selected)
      .map(a => a.id);
  }

  setSelected(id: string, on: boolean): boolean {
    const entry = this.entries.get(id);

    if (entry === undefined || entry.selected === on || (on && entry.disabledReason !== null)) return false;

    entry.selected = on;

    if (on && !this.map.hasLayer(entry.source.layer)) this.map.addLayer(entry.source.layer);
    if (!on && this.map.hasLayer(entry.source.layer)) this.map.removeLayer(entry.source.layer);

    this.events.emit("change", { entry: snapshotOf(entry), reason: "selected" });

    return true;
  }

  setOpacity(id: string, value: number): void {
    const entry = this.entries.get(id);

    if (entry === undefined) return;

    entry.opacity = Math.min(1, Math.max(0, value));
    this.applyOpacityTo(entry);
    this.events.emit("change", { entry: snapshotOf(entry), reason: "opacity" });
  }

  setStatus(id: string, status: LayerStatus, error: string | null = null, httpStatus: number | null = null): void {
    const entry = this.entries.get(id);

    if (entry === undefined) return;

    entry.status = status;
    entry.error = error;
    entry.httpStatus = httpStatus;

    if (status === "error") this.events.emit("error", { entry: snapshotOf(entry), status: httpStatus, message: error ?? "" });

    this.emitList();
  }

  setCount(id: string, count: number | null, countLabel: string | null = null, truncated = false): void {
    const entry = this.entries.get(id);

    if (entry === undefined) return;

    entry.count = count;
    entry.countLabel = countLabel;
    entry.truncated = truncated;
  }

  setDisabledReason(id: string, reason: string | null): void {
    const entry = this.entries.get(id);

    if (entry === undefined || entry.disabledReason === reason) return;

    entry.disabledReason = reason;

    if (reason !== null && entry.selected) {
      entry.selected = false;

      if (this.map.hasLayer(entry.source.layer)) this.map.removeLayer(entry.source.layer);
    }

    this.emitList();
  }

  updateEntry(id: string, entry: CatalogueEntry): boolean {
    const current = this.entries.get(id);

    if (current === undefined || current.entry === entry) return false;

    const catalogueCount = current.entry.source?.counts?.total ?? null;

    if (current.count === catalogueCount) current.count = entry.source?.counts?.total ?? null;

    current.entry = entry;
    current.title = entry.name;
    current.minZoom = entry.minZoom;
    current.maxZoom = entry.maxZoom;

    if (current.source.update) current.source.update(entry);
    else current.source.entry = entry;

    return true;
  }

  setTitle(id: string, title: string): void {
    const entry = this.entries.get(id);

    if (entry === undefined || entry.title === title) return;

    entry.title = title;
    this.emitList();
  }

  emitLoaded(): void {
    this.events.emit("loaded", { entries: this.snapshots() });
    this.emitList();
  }

  emitList(): void {
    this.events.emit("list", { entries: this.snapshots() });
  }

  inZoomRange(entry: RegistryEntry, zoom: number): boolean {
    if (entry.minZoom !== null && zoom < entry.minZoom) return false;
    if (entry.maxZoom !== null && zoom > entry.maxZoom) return false;

    return true;
  }

  dispose(): void {
    for (const id of [...this.order]) this.remove(id);

    this.events.clear();
  }

  private applyOpacityTo(entry: RegistryEntry): void {
    if (entry.pane !== null) {
      const pane = this.map.getPane(entry.pane);

      if (pane) pane.style.opacity = String(entry.opacity);

      return;
    }

    if (entry.source.setOpacity) {
      entry.source.setOpacity(entry.opacity);
      return;
    }

    applyOpacity(entry.source.layer, entry.opacity);
  }
}
